import { useState, useEffect, useRef } from 'react';
import { showNotificationToast, openPostModal } from '../utils/notificationUtils';
import { UrgentCaseNotificationModal } from '../components/modals';
import { clinicAppointmentsAPI, outpatientAPI } from '../services/api';
import { useNavigate, useParams } from 'react-router-dom';
import { useEcho } from '@laravel/echo-react';
import { isEchoConfigured } from '../services/echo';
import { handleNotificationRouting } from '../utils/notificationRouting';
import {
  handleMedicalMessageReceivedNotification,
  handleMedicalMessageSentFromMySideNotification,
  isMedicalConversationNotification
} from './notifications/medicalConversationHandlers';
export const useNotifications = () => {
  const [isPermissionGranted, setIsPermissionGranted] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState('unknown');
  const [isLoading, setIsLoading] = useState(false);
  const [echoConnected, setEchoConnected] = useState(false);
  const processedNotificationIdsRef = useRef(new Set());
  const navigate = useNavigate();
  const params = useParams();

  // Helper function to get clinicId
  const getClinicId = () => {
    if (params?.clinicId) return params.clinicId;
    const pathMatch = window.location.pathname.match(/\/medical\/clinic\/(\d+)/);
    if (pathMatch) return pathMatch[1];
    try {
      const userData = localStorage.getItem('user_data');
      if (userData && userData !== 'undefined' && userData !== 'null') {
        const parsed = JSON.parse(userData);
        return parsed.clinic_id;
      }
    } catch (e) {
      // Ignore errors
    }
    return null;
  };
  // Urgent case modal state
  const [urgentCaseModalOpen, setUrgentCaseModalOpen] = useState(false);
  const [urgentCaseData, setUrgentCaseData] = useState(null);

  // Telemedicine modal state
  const [telemedicineModalOpen, setTelemedicineModalOpen] = useState(false);
  const [telemedicineData, setTelemedicineData] = useState(null);

  // Notification permission modal state
  const [notificationPermissionModalOpen, setNotificationPermissionModalOpen] = useState(false);

  // Appointment notification modal state
  const [appointmentModalOpen, setAppointmentModalOpen] = useState(false);
  const [appointmentModalData, setAppointmentModalData] = useState(null);

  // Outpatient visit notification modal state
  const [outpatientModalOpen, setOutpatientModalOpen] = useState(false);
  const [outpatientModalData, setOutpatientModalData] = useState(null);

  // Inpatient visit notification modal state
  const [inpatientModalOpen, setInpatientModalOpen] = useState(false);
  const [inpatientModalData, setInpatientModalData] = useState(null);

  // Post deleted by admin notification modal state
  const [postDeletedModalOpen, setPostDeletedModalOpen] = useState(false);
  const [postDeletedModalData, setPostDeletedModalData] = useState(null);

  // Listen for custom event to re-open the post-deleted modal (e.g. from dropdown click)
  useEffect(() => {
    const handleOpenModal = (event) => {
      const detail = event.detail || {};
      setPostDeletedModalData({
        reasons: detail.reasons || null,
        reason: detail.reason || null,
        reason_labels: detail.reason_labels || null,
      });
      setPostDeletedModalOpen(true);
    };
    window.addEventListener('open-post-deleted-modal', handleOpenModal);
    return () => window.removeEventListener('open-post-deleted-modal', handleOpenModal);
  }, []);

  // Check if browser notifications are supported
  const isNotificationSupported = () => {
    return 'Notification' in window;
  };

  // Request browser notification permission
  const requestPermission = async () => {
    try {
      if (!isNotificationSupported()) {
        return false;
      }

      const permission = await Notification.requestPermission();
      setPermissionStatus(permission);
      setIsPermissionGranted(permission === 'granted');

      if (permission === 'granted') {
      } else {
      }

      return permission === 'granted';
    } catch (error) {
      console.error('❌ Error requesting notification permission:', error);
      return false;
    }
  };


  // Get current user ID from localStorage (will be updated when user data changes)
  const [userId, setUserId] = useState(() => {
    const userData = JSON.parse(localStorage.getItem('user_data') || '{}');
    return userData.id || userData.user_id;
  });

  // Update userId when localStorage changes
  useEffect(() => {
    const checkUserData = () => {
      const userData = JSON.parse(localStorage.getItem('user_data') || '{}');
      const newUserId = userData.id || userData.user_id;
      if (newUserId !== userId) {
        setUserId(newUserId);
      }
    };

    // Check on mount and set up interval
    checkUserData();
    const interval = setInterval(checkUserData, 1000);
    return () => clearInterval(interval);
  }, [userId]);

  // Use @laravel/echo-react hook to subscribe to notifications
  // Note: useEcho automatically adds 'private-' prefix for private channels
  // So we should pass the channel name WITHOUT the 'private-' prefix
  const channelName = userId ? `App.Models.User.${userId}` : null;
  const isEchoReady = isEchoConfigured();

  // Check if access token exists (required for private channel auth)
  const hasToken = typeof window !== 'undefined' && localStorage.getItem('access_token');

  // Subscribe to notification channel using useEcho hook
  // Only subscribe if Echo is configured, we have a channel name, and user has a token
  useEcho(
    isEchoReady && channelName && hasToken ? channelName : null,
    '.notification.created',
    (notification) => {
      handleNotificationReceived(notification);
    }
  );

  // Subscribe to Echo notification channel (legacy method for backward compatibility)
  const subscribeToNotifications = async () => {
    try {
      // Check if Echo is configured
      if (!isEchoConfigured()) {
        const appKey = import.meta.env.VITE_REVERB_APP_KEY;
        if (!appKey) {
          if (import.meta.env.DEV) {
          }
        } else {
          console.warn('⚠️ Echo not configured despite Reverb configuration. WebSocket connections will not work.');
        }
        setEchoConnected(false);
        return;
      }

      if (!userId) {
        setEchoConnected(false);
        return;
      }

      setEchoConnected(true);
    } catch (error) {
      console.error('❌ Error subscribing to notifications:', error);
      setEchoConnected(false);
    }
  };

  // Initialize Echo connection and listen for notifications
  useEffect(() => {
    const initializeNotifications = async () => {
      setIsLoading(true);
      try {
        // Check notification permission
        const permission = Notification.permission;
        setPermissionStatus(permission);
        setIsPermissionGranted(permission === 'granted');

        // Show modal if permission is denied or default
        if (permission === 'denied' || permission === 'default') {
          setTimeout(() => {
            setNotificationPermissionModalOpen(true);
          }, 2000);
        }

        // Subscribe to notifications
        await subscribeToNotifications();

      } catch (error) {
        console.error('Error initializing notifications:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeNotifications();

    // Listen for user login events to re-subscribe
    const handleUserLogin = () => {
      subscribeToNotifications();
    };

    window.addEventListener('user-logged-in', handleUserLogin);

    return () => {
      window.removeEventListener('user-logged-in', handleUserLogin);
    };
  }, []);

  // Handle notification received via Echo
  const handleNotificationReceived = (notification) => {
    try {
      console.log('🔔 [useNotifications] RAW notification received:', notification);

      const notificationId = notification?.id || notification?.notification_id || notification?.data?.id || notification?.data?.notification_id;
      if (notificationId) {
        const notificationKey = `${notification.type || 'notification'}:${notificationId}`;
        if (processedNotificationIdsRef.current.has(notificationKey)) {
          console.log('🔁 [useNotifications] Duplicate notification ignored:', notificationKey);
          return;
        }

        processedNotificationIdsRef.current.add(notificationKey);
        if (processedNotificationIdsRef.current.size > 200) {
          const [oldestKey] = processedNotificationIdsRef.current;
          processedNotificationIdsRef.current.delete(oldestKey);
        }
      }

      // ── FAST PATH: Deletion events are dispatched immediately ──────────────
      const isDeleteEvent = notification.type === 'message_deleted' ||
        notification.is_deleted === true ||
        (notification.data && (notification.data.type === 'message_deleted' || notification.data.is_deleted === true));

      if (isDeleteEvent) {
        const data = notification.data || notification;
        const msgId = data.message_id || notification.message_id;
        const convId = data.conversation_id || notification.conversation_id || notification.target_id;
        console.log('🗑️ [useNotifications] FAST PATH deletion dispatch:', { msgId, convId });
        if (msgId) {
          window.dispatchEvent(new CustomEvent('message-deleted-sync', {
            detail: {
              messageId: msgId,
              message_id: msgId,
              conversationId: convId,
              conversation_id: convId,
              conversationType: data.conversation_type || notification.conversation_type || 'social',
            }
          }));
        }
        // Don't return — still fall through to handleNotificationData for any other processing
      }

      // Check if this is a sender notification
      // Use is_sender_notification flag (unified message_received type)
      const isSenderNotification = notification.is_sender_notification === true;

      // For sender notifications, check if conversation is open before showing browser notification
      if (isSenderNotification) {
        // Handle both direct structure and nested data structure
        let conversation_id = notification.conversation_id ||
          notification.target_id ||
          (notification.action_url ? notification.action_url.split('/chat/')[1]?.split('/')[0] : null);

        // If data is nested, try to extract from there
        if (!conversation_id && notification.data) {
          try {
            const parsedData = typeof notification.data === 'string'
              ? JSON.parse(notification.data)
              : notification.data;
            conversation_id = parsedData.conversation_id || parsedData.target_id;
          } catch (e) {
            // Ignore parse errors
          }
        }

        if (conversation_id) {
          const currentPath = window.location.pathname;
          const activeConversationId = localStorage.getItem('active_conversation_id');

          const isConversationOpen = (currentPath.includes('/chat/') &&
            (currentPath.includes(`/chat/${conversation_id}`) ||
              currentPath.includes(`chat/${conversation_id}/`) ||
              currentPath === `/chat/${conversation_id}`)) ||
            (activeConversationId && activeConversationId.toString() === conversation_id.toString());

          if (isConversationOpen) {
            // Still process the notification data for syncing, but don't show browser notification
            handleNotificationData(notification);
            return;
          }
        }
      }

      // Show browser notification if permission is granted
      // All notifications now have standardized structure: { title, content, type, priority, action_url, image, sender_name, sender_image }
      const suppressUserNotification =
        notification.suppress_user_notification === true ||
        notification.suppress_user_notification === 'true' ||
        notification.suppress_user_notification === 1 ||
        notification.suppress_user_notification === '1' ||
        notification.data?.suppress_user_notification === true ||
        notification.data?.suppress_user_notification === 'true' ||
        notification.data?.suppress_user_notification === 1 ||
        notification.data?.suppress_user_notification === '1';

      if (!suppressUserNotification && isPermissionGranted && Notification.permission === 'granted') {
        const notificationTitle = notification.title || 'New Notification';
        const notificationBody = notification.content || notification.message || 'You have a new notification';
        const notificationIcon = notification.image || notification.sender_image || '/favicon.ico';

        const browserNotification = new Notification(notificationTitle, {
          body: notificationBody,
          icon: notificationIcon,
          badge: '/favicon.ico',
          tag: notification.id,
          data: notification,
        });

        browserNotification.onclick = () => {
          window.focus();
          if (notification.action_url) {
            navigate(notification.action_url);
          }
          browserNotification.close();
        };
      }

      // Process notification data
      handleNotificationData(notification);
    } catch (error) {
      console.error('❌ Error handling notification:', error);
    }
  };


  // Process pending messages when app becomes active
  useEffect(() => {
    const processPendingMessages = () => {
      try {
        const pendingMessages = JSON.parse(localStorage.getItem('pendingMessages') || '[]');

        if (pendingMessages.length > 0) {

          // Process each pending message
          pendingMessages.forEach((message) => {
            try {

              // Handle urgent medical cases immediately
              if (message.type === 'urgent_medical_case') {
                handleUrgentCaseNotification(message);
              } else {
                // Handle other notification types
                handleNotificationData(JSON.stringify(message));
              }
            } catch (error) {
              console.error('❌ Error processing pending message:', error, message);
              // Continue processing other messages
            }
          });

          // Clear pending messages after processing
          localStorage.removeItem('pendingMessages');

          // Clear badge counts
          localStorage.removeItem('badgeCount');
          localStorage.removeItem('chat_badge_count');

          if ('setAppBadge' in navigator) {
            navigator.setAppBadge(0);
          }

        }
      } catch (error) {
        console.error('❌ Error processing pending messages:', error);
      }
    };

    // Process pending messages when component mounts (app becomes active)
    processPendingMessages();

    // Also listen for visibility change to process messages when tab becomes active
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        processPendingMessages();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Listen for user login event to send FCM token
  useEffect(() => {
    const handleUserLogin = async (event) => {

      const needNewFcmToken = event.detail?.needNewFcmToken || false;

      if (needNewFcmToken) {
        // FCM token regeneration removed - using Reverb for notifications instead
      } else {
        // Normal flow - don't send token to server automatically
      }
    };

    window.addEventListener('user-logged-in', handleUserLogin);

    return () => {
      window.removeEventListener('user-logged-in', handleUserLogin);
    };
  }, []); // Only set up event listener once on mount

  const processMedicalMessageNotification = (notification) => {
    handleMedicalMessageReceivedNotification({
      notificationData: notification,
      isPermissionGranted,
      navigate,
      showNotificationToast
    });
  };

  const processMedicalMessageSentNotification = (notification) => {
    handleMedicalMessageSentFromMySideNotification({
      notificationData: notification
    });
  };

  // Unified message notification handler - works on any page and handles all message types
  const handleMessageReceived = (notificationData) => {
    try {

      // Check if this is actually a deletion notification masquerading as a message
      const isDeleted = notificationData.is_deleted === true ||
        notificationData.type === 'message_deleted' ||
        (notificationData.data && (notificationData.data.is_deleted === true || notificationData.data.type === 'message_deleted'));

      if (isDeleted) {
        handleMessageDeleted(notificationData);
        return;
      }

      if (isMedicalConversationNotification(notificationData)) {
        processMedicalMessageNotification(notificationData);
        return;
      }

      const isSenderNotification = notificationData.is_sender_notification === true;

      let actualNotificationData = notificationData;
      if (notificationData.data && typeof notificationData.data === 'string' && notificationData.data !== 'undefined') {
        try {
          actualNotificationData = JSON.parse(notificationData.data);
        } catch (error) {
          console.error('❌ Error parsing nested message data:', error);
        }
      }

      if (isSenderNotification) {
        const currentDeviceKey = localStorage.getItem('device_id');
        const normalizedCurrentDeviceKey = currentDeviceKey ? currentDeviceKey.toString() : null;
        const notificationDeviceKey = actualNotificationData?.deviceKey || notificationData.deviceKey;
        if (notificationDeviceKey && normalizedCurrentDeviceKey && notificationDeviceKey === normalizedCurrentDeviceKey) {
          return;
        }
      }

      const conversationType = actualNotificationData.conversation_type ||
        notificationData.conversation_type ||
        'social';

      const sender = actualNotificationData.sender || {
        id: actualNotificationData.sender_id || notificationData.sender_id,
        name: actualNotificationData.sender_name || notificationData.sender_name || 'Someone',
        image: actualNotificationData.sender_image || notificationData.sender_image || null
      };

      let messageContent = actualNotificationData.message ||
        actualNotificationData.content ||
        notificationData.content ||
        notificationData.message ||
        null;

      let mediaFromMessage = null;
      if (messageContent && typeof messageContent === 'string' && messageContent.trim().startsWith('{')) {
        try {
          const parsedMessage = JSON.parse(messageContent);
          if (parsedMessage && parsedMessage.media && Array.isArray(parsedMessage.media)) {
            mediaFromMessage = parsedMessage.media;
            messageContent = null;
          }
        } catch (error) {
          // Ignore parse errors and continue with the raw message
        }
      }

      let media = mediaFromMessage || actualNotificationData.media || notificationData.media || [];
      media = media.map(m => {
        if (!m.file_type) {
          if (m.is_image) {
            m.file_type = 'image';
          } else if (m.is_video) {
            m.file_type = 'video';
          } else if (m.is_document) {
            m.file_type = 'document';
          } else if (m.is_audio) {
            m.file_type = 'audio';
          } else {
            m.file_type = 'file';
          }
        }
        return m;
      });

      const hasMedia = media.length > 0;

      const conversationId = actualNotificationData.conversation_id ||
        notificationData.conversation_id ||
        actualNotificationData.target_id ||
        notificationData.target_id ||
        sender.id ||
        (actualNotificationData.action_url ?
          actualNotificationData.action_url.split('/chat/')[1]?.split('/')[0] : null) ||
        (notificationData.action_url ?
          notificationData.action_url.split('/chat/')[1]?.split('/')[0] : null);
      const currentPath = window.location.pathname;
      const activeConversationId = localStorage.getItem('active_conversation_id');
      const isOnChatPage = currentPath.includes('/chat');

      // Only check if conversation is open if we're actually on the chat page
      // This prevents false positives when active_conversation_id is set but user is on a different page
      const isConversationOpenInPath = conversationId && isOnChatPage && (
        currentPath === `/chat/${conversationId}` ||
        currentPath.includes(`/chat/${conversationId}/`) ||
        currentPath.includes(`chat/${conversationId}`)
      );
      const isConversationOpen = isConversationOpenInPath ||
        (isOnChatPage && activeConversationId && conversationId &&
          activeConversationId.toString() === conversationId.toString());

      if (isSenderNotification) {
        if (!conversationId) {
          return;
        }

        let notificationBody = messageContent;
        if (!notificationBody && media && media.length > 0) {
          const imageCount = media.filter(m => m.is_image || m.file_type === 'image').length;
          const videoCount = media.filter(m => m.is_video || m.file_type === 'video').length;
          const docCount = media.filter(m => m.is_document || m.file_type === 'document').length;

          const parts = [];
          if (imageCount > 0) parts.push(`${imageCount} image${imageCount > 1 ? 's' : ''}`);
          if (videoCount > 0) parts.push(`${videoCount} video${videoCount > 1 ? 's' : ''}`);
          if (docCount > 0) parts.push(`${docCount} document${docCount > 1 ? 's' : ''}`);

          notificationBody = parts.length > 0 ? `Sent ${parts.join(', ')}` : 'Your message was sent';
        } else if (!notificationBody) {
          notificationBody = 'Your message was sent';
        }

        const messageSyncEvent = new CustomEvent('message-sent-sync', {
          detail: {
            type: 'message_received',
            conversationId: conversationId,
            conversationType: conversationType, // Include conversation type
            message: messageContent,
            content: notificationBody,
            media: media || [],
            timestamp: new Date().toISOString(),
            isSenderNotification: true,
            deviceKey: actualNotificationData?.deviceKey || notificationData.deviceKey || null,
            notificationData: notificationData
          }
        });
        window.dispatchEvent(messageSyncEvent);

        if (isConversationOpen) {
          const refreshEvent = new CustomEvent('refresh-chat-messages', {
            detail: {
              conversationId: conversationId,
              conversationType: conversationType, // Include conversation type
              message: messageContent,
              content: notificationBody,
              media: media || [],
              isSenderNotification: true
            }
          });
          window.dispatchEvent(refreshEvent);
          return;
        }

        if (isPermissionGranted && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
          const notificationTitle = notificationData.title || 'Message sent';
          const notificationIcon = notificationData.sender_image || notificationData.image || '/favicon.ico';

          const browserNotification = new Notification(notificationTitle, {
            body: notificationBody,
            icon: notificationIcon,
            badge: '/favicon.ico',
            tag: `message-sent-${conversationId}-${Date.now()}`,
            data: {
              ...notificationData,
              conversation_id: conversationId,
              isSenderNotification: true,
              media: media
            },
          });

          browserNotification.onclick = () => {
            window.focus();
            if (notificationData.action_url) {
              navigate(notificationData.action_url);
            }
            browserNotification.close();
          };
        }
        return;
      }

      const badgeKey = 'chat_badge_count';
      const currentBadgeCount = parseInt(localStorage.getItem(badgeKey) || '0', 10);
      let newChatCount;

      if (actualNotificationData.unread_count !== undefined && actualNotificationData.unread_count !== null) {
        newChatCount = actualNotificationData.unread_count;
      } else if (notificationData.content && !isNaN(parseInt(notificationData.content))) {
        newChatCount = parseInt(notificationData.content, 10);
      } else {
        newChatCount = currentBadgeCount + 1;
      }

      if (location.pathname.includes('/chat')) {
        newChatCount = 0;
      }
      if (newChatCount !== null && newChatCount !== undefined) {
        localStorage.setItem(badgeKey, newChatCount.toString());
      }

      const badgeUpdateEvent = new CustomEvent('chat-badge-update', {
        detail: {
          count: newChatCount,
          message: messageContent,
          sender: sender,
          media: media,
          hasMedia: hasMedia,
          timestamp: new Date().toISOString(),
          conversationType: conversationType
        }
      });
      window.dispatchEvent(badgeUpdateEvent);

      // Update chat list directly
      const updateChatListEvent = new CustomEvent('update-chat-list-direct', {
        detail: {
          id: actualNotificationData.message_id || notificationData.message_id || actualNotificationData.id || notificationData.id,
          messageId: actualNotificationData.message_id || notificationData.message_id || actualNotificationData.id || notificationData.id,
          senderId: sender?.id || actualNotificationData.sender_id || notificationData.sender_id,
          conversationId: conversationId,
          conversation_id: conversationId,
          conversationType: conversationType,
          conversation_type: conversationType,
          message: messageContent,
          content: messageContent,
          media: media || [],
          sender: sender,
          timestamp: new Date().toISOString(),
          isRedundant: isOnChatPage || isConversationOpen
        }
      });
      window.dispatchEvent(updateChatListEvent);

      // Create message update event
      // ✅ FIXED: Extract the real numeric DB message_id.
      // When notification.data is an OBJECT (not a string), actualNotificationData === notificationData,
      // so data.message_id is only accessible via notificationData.data.message_id.
      const realMessageId =
        actualNotificationData.message_id ||
        notificationData.message_id ||
        (notificationData.data && typeof notificationData.data === 'object' ? notificationData.data.message_id : null) ||
        actualNotificationData.id ||
        notificationData.id; // UUID fallback (should only be used as last resort)

      const messageUpdateEvent = new CustomEvent('new-message-received', {
        detail: {
          id: realMessageId,
          message_id: realMessageId, // explicitly add for addNewMessageToConversation fallback
          message: messageContent,
          content: messageContent,
          sender: sender,
          senderId: sender?.id || actualNotificationData.sender_id,
          media: media,
          hasMedia: hasMedia,
          conversationId: conversationId,
          conversationType: conversationType,
          messageId: realMessageId,
          timestamp: new Date().toISOString(),
          mediaPreview: hasMedia && media.length > 0 ? {
            url: media[0].url,
            type: media[0].file_type,
            isImage: media[0].is_image,
            isVideo: media[0].is_video,
            name: media[0].file_name
          } : null
        }
      });

      // Always fire new-message-received if we are on the chat page
      // This allows the Chat component to decide if it should update its main window
      // regardless of the localStorage/path sync status in the notification hook.
      if (isOnChatPage || isConversationOpen) {
        window.dispatchEvent(messageUpdateEvent);

        // If it's explicitly open, we're done (no toast needed)
        if (isConversationOpen) {
          return;
        }
      }

      const senderName = sender?.name || 'Someone';
      const senderImage = sender?.image || null;

      let notificationMessage = 'New message received';
      if (messageContent) {
        notificationMessage = messageContent;
      } else if (hasMedia) {
        const mediaTypes = media.map(m => m.file_type).filter((type, index, arr) => arr.indexOf(type) === index);
        if (mediaTypes.length === 1) {
          const count = media.length;
          const type = mediaTypes[0];
          if (type === 'image') {
            notificationMessage = count === 1 ? 'Photo' : `${count} Photos`;
          } else if (type === 'video') {
            notificationMessage = count === 1 ? 'Video' : `${count} Videos`;
          } else if (type === 'document') {
            notificationMessage = count === 1 ? 'Document' : `${count} Documents`;
          } else if (type === 'audio') {
            notificationMessage = count === 1 ? 'Voice message' : `${count} Voice messages`;
          } else {
            notificationMessage = count === 1 ? 'File' : `${count} Files`;
          }
        } else {
          notificationMessage = `${media.length} Files`;
        }
      } else if (actualNotificationData.content && !isNaN(actualNotificationData.content)) {
        notificationMessage = `You have ${actualNotificationData.content} new message${parseInt(actualNotificationData.content) > 1 ? 's' : ''}`;
      }

      const notificationOptions = {
        type: 'info',
        title: actualNotificationData.title || senderName,
        message: notificationMessage,
        image: senderImage,
        duration: 8000,
        clickable: false
      };

      if (hasMedia && media.length > 0) {
        notificationOptions.mediaPreview = {
          url: media[0].url,
          type: media[0].file_type,
          isImage: media[0].is_image,
          isVideo: media[0].is_video,
          name: media[0].file_name
        };
      }

      const suppressUserNotification =
        actualNotificationData.suppress_user_notification === true ||
        actualNotificationData.suppress_user_notification === 'true' ||
        actualNotificationData.suppress_user_notification === 1 ||
        actualNotificationData.suppress_user_notification === '1' ||
        notificationData.suppress_user_notification === true ||
        notificationData.suppress_user_notification === 'true' ||
        notificationData.suppress_user_notification === 1 ||
        notificationData.suppress_user_notification === '1';

      if (!suppressUserNotification) {
        showNotificationToast(actualNotificationData, notificationOptions);
      }

    } catch (error) {
      console.error('❌ Error handling message notification:', error);
    }
  };

  // Handle messages read notification (when another user marks messages as read)
  // Handle messages read notification (when another user marks messages as read)
  const handleMessagesReadNotification = (notificationData) => {
    try {
      console.log('🔔 [useNotifications] Handling messages_read notification:', notificationData);

      // Extract conversation_id from notification data
      // Check multiple possible paths
      const conversationId = notificationData.conversation_id ||
        notificationData.conversationId ||
        notificationData.data?.conversation_id ||
        notificationData.data?.conversationId;

      if (!conversationId) {
        console.warn('❌ [useNotifications] messages_read missing conversation_id', notificationData);
        return;
      }

      // Extract conversation_type (defaults to 'social')
      const conversationType = notificationData.conversation_type ||
        notificationData.conversationType ||
        notificationData.data?.conversation_type ||
        'social';

      console.log('🚀 [useNotifications] Dispatching messages-read event for conversation:', conversationId);

      // Dispatch CustomEvent to notify chat components
      const messagesReadEvent = new CustomEvent('messages-read', {
        detail: {
          conversationId: conversationId,
          conversation_id: conversationId,
          conversationType: conversationType,
          conversation_type: conversationType,
          timestamp: new Date().toISOString()
        }
      });
      window.dispatchEvent(messagesReadEvent);

      // Update chat list unread count (set to 0 since messages are now read)
      const updateChatListEvent = new CustomEvent('update-chat-list-direct', {
        detail: {
          conversationId: conversationId,
          conversation_id: conversationId,
          conversationType: conversationType,
          conversation_type: conversationType,
          unreadCount: 0
        }
      });
      window.dispatchEvent(updateChatListEvent);

    } catch (error) {
      console.error('❌ Error handling messages_read notification:', error);
    }
  };

  // Handle message sent from my side notification (for syncing across devices)
  const handleMessageSentFromMySide = (notificationData) => {
    if (isMedicalConversationNotification(notificationData)) {
      processMedicalMessageSentNotification(notificationData);
      return;
    }

    try {
      const currentDeviceId = localStorage.getItem("device_id");

      let actualNotificationData = notificationData;
      if (notificationData.data && typeof notificationData.data === 'string' && notificationData.data !== 'undefined') {
        try {
          actualNotificationData = JSON.parse(notificationData.data);
        } catch (error) {
          console.error('❌ Error parsing nested message data:', error);
        }
      }

      const notificationDeviceKey = actualNotificationData?.deviceKey ||
        actualNotificationData?.device_key ||
        notificationData.deviceKey ||
        notificationData.device_key;

      if (currentDeviceId && notificationDeviceKey &&
        currentDeviceId.toString() === notificationDeviceKey.toString()) {
        return;
      }


      const conversationType = actualNotificationData.conversation_type ||
        notificationData.conversation_type ||
        'social';

      const sender = actualNotificationData.sender || {
        id: actualNotificationData.sender_id || notificationData.sender_id,
        name: actualNotificationData.sender_name || notificationData.sender_name || 'Someone',
        image: actualNotificationData.sender_image || notificationData.sender_image || null
      };

      let messageContent = actualNotificationData.message ||
        actualNotificationData.content ||
        notificationData.content ||
        notificationData.message ||
        null;

      let mediaFromMessage = null;
      if (messageContent && typeof messageContent === 'string' && messageContent.trim().startsWith('{')) {
        try {
          const parsedMessage = JSON.parse(messageContent);
          if (parsedMessage && parsedMessage.media && Array.isArray(parsedMessage.media)) {
            mediaFromMessage = parsedMessage.media;
            messageContent = null;
          }
        } catch (error) {
          // Ignore parse errors
        }
      }

      let media = mediaFromMessage || actualNotificationData.media || notificationData.media || [];
      media = media.map(m => {
        if (!m.file_type) {
          if (m.is_image) {
            m.file_type = 'image';
          } else if (m.is_video) {
            m.file_type = 'video';
          } else if (m.is_document) {
            m.file_type = 'document';
          } else if (m.is_audio) {
            m.file_type = 'audio';
          } else {
            m.file_type = 'file';
          }
        }
        return m;
      });

      const hasMedia = media.length > 0;

      const conversationId = actualNotificationData.conversation_id ||
        notificationData.conversation_id ||
        actualNotificationData.target_id ||
        notificationData.target_id ||
        sender.id ||
        (actualNotificationData.action_url ?
          actualNotificationData.action_url.split('/chat/')[1]?.split('/')[0] : null) ||
        (notificationData.action_url ?
          notificationData.action_url.split('/chat/')[1]?.split('/')[0] : null);

      const currentPath = window.location.pathname;
      const activeConversationId = localStorage.getItem('active_conversation_id');
      const isOnChatPage = currentPath.includes('/chat');

      // Only check if conversation is open if we're actually on the chat page
      // This prevents false positives when active_conversation_id is set but user is on a different page
      const isConversationOpenInPath = conversationId && isOnChatPage && (
        currentPath === `/chat/${conversationId}` ||
        currentPath.includes(`/chat/${conversationId}/`) ||
        currentPath.includes(`chat/${conversationId}`)
      );
      const isConversationOpen = isConversationOpenInPath ||
        (isOnChatPage && activeConversationId && conversationId &&
          activeConversationId.toString() === conversationId.toString());

      let notificationBody = messageContent;
      if (!notificationBody && media && media.length > 0) {
        const imageCount = media.filter(m => m.is_image || m.file_type === 'image').length;
        const videoCount = media.filter(m => m.is_video || m.file_type === 'video').length;
        const docCount = media.filter(m => m.is_document || m.file_type === 'document').length;

        const parts = [];
        if (imageCount > 0) parts.push(`${imageCount} image${imageCount > 1 ? 's' : ''}`);
        if (videoCount > 0) parts.push(`${videoCount} video${videoCount > 1 ? 's' : ''}`);
        if (docCount > 0) parts.push(`${docCount} document${docCount > 1 ? 's' : ''}`);

        notificationBody = parts.length > 0 ? `Sent ${parts.join(', ')}` : 'Your message was sent';
      } else if (!notificationBody) {
        notificationBody = 'Your message was sent';
      }

      if (!conversationId) {
        return;
      }

      // Update chat list directly without reloading
      const updateChatListEvent = new CustomEvent('update-chat-list-direct', {
        detail: {
          senderId: sender?.id || actualNotificationData.sender_id || notificationData.sender_id,
          conversationId: conversationId,
          conversation_id: conversationId,
          conversationType: conversationType,
          conversation_type: conversationType,
          message: messageContent,
          content: notificationBody,
          media: media || [],
          sender: sender,
          timestamp: new Date().toISOString()
        }
      });
      window.dispatchEvent(updateChatListEvent);

      // If conversation is open, refresh messages directly without reloading
      if (isConversationOpen) {
        const refreshEvent = new CustomEvent('refresh-chat-messages', {
          detail: {
            conversationId: conversationId,
            conversationType: conversationType, // Include conversation type
            message: messageContent,
            content: notificationBody,
            media: media || [],
            isSenderNotification: true
          }
        });
        window.dispatchEvent(refreshEvent);
        return;
      }

      // Dispatch message sync event for other components
      const messageSyncEvent = new CustomEvent('message-sent-sync', {
        detail: {
          type: 'message_received',
          conversationId: conversationId,
          conversationType: conversationType, // Include conversation type
          message: messageContent,
          content: notificationBody,
          media: media || [],
          timestamp: new Date().toISOString(),
          isSenderNotification: true,
          deviceKey: notificationDeviceKey || null,
          notificationData: notificationData
        }
      });
      window.dispatchEvent(messageSyncEvent);

    } catch (error) {
    }
  };

  // Dedicated handler for message deletion notifications
  const handleMessageDeleted = (notificationData) => {
    try {
      console.log('🗑️ [Message] Delete notification received:', notificationData);

      // Extract data (handle both direct and nested structures)
      const data = notificationData.data || notificationData;
      const messageId = data.message_id || notificationData.message_id || data.id || notificationData.id;
      const conversationId = data.conversation_id || notificationData.conversation_id || data.target_id || notificationData.target_id;
      const conversationType = data.conversation_type || notificationData.conversation_type || 'social';

      console.log('🗑️ [useNotifications] Extracted for deletion:', { messageId, conversationId, conversationType });

      if (!messageId || !conversationId) {
        console.warn('🗑️ [Message] Missing message_id or conversation_id in deletion notification');
        return;
      }
      // Dispatch event for chat components to remove the message
      // This works as a backup/sync for when WebSocket events are missed or received via other channels

      const event = new CustomEvent('message-deleted-sync', {
        detail: {
          messageId,
          message_id: messageId,
          conversationId,
          conversation_id: conversationId,
          conversationType,
          timestamp: new Date().toISOString()
        }
      });
      window.dispatchEvent(event);

      // Also dispatch a general notification received event for components that handle multiple types
      window.dispatchEvent(new CustomEvent('notification-received', {
        detail: {
          ...notificationData,
          type: 'message_deleted',
          message_id: messageId,
          conversation_id: conversationId,
          conversation_type: conversationType
        }
      }));

    } catch (error) {
      console.error('❌ [Message] Error handling message deleted notification:', error);
    }
  };

  // Dedicated handler for video processing completed notifications
  const handleVideoProcessingCompleted = (notificationData) => {
    try {
      console.log('🎥 [Video] Processing completed notification received:', notificationData);

      // Extract data (handle both direct and nested structures)
      const videoData = notificationData.data || notificationData;
      const postId = videoData.post_id || notificationData.post_id;

      console.log('🎥 [Video] Extracted data:', {
        postId,
        mediaId: videoData.media_id,
        mediaUrl: videoData.media_url
      });

      // Create normalized notification object with post_id at root level
      // This ensures the post modal opens correctly
      const normalizedNotification = {
        ...notificationData,
        type: 'video_processing_completed',
        post_id: postId, // Ensure post_id is at root level
        title: '✅ Video Ready',
        message: videoData.message || 'Your video has been processed and is ready to view!'
      };

      // Show success toast (post modal will open automatically via notificationUtils)
      showNotificationToast(normalizedNotification, {
        type: 'success',
        duration: 6000
      });

      // Dispatch event for SocialFeed to update
      const event = new CustomEvent('video-processing-completed', {
        detail: {
          post_id: postId,
          media_id: videoData.media_id,
          media_url: videoData.media_url,
          thumbnail: videoData.thumbnail,
          resolutions: videoData.resolutions,
          duration: videoData.duration,
          timestamp: new Date().toISOString()
        }
      });

      console.log('🎥 [Video] Dispatching custom event:', event.detail);
      window.dispatchEvent(event);

    } catch (error) {
      console.error('❌ [Video] Error handling video completion notification:', error);
    }
  };

  // Handle notification data - all notifications now have standardized structure
  const handleNotificationData = (data) => {
    try {
      // Handle both JSON string and object data
      let notificationData;
      if (typeof data === 'string') {
        notificationData = JSON.parse(data);
      } else {
        notificationData = data;
      }

      // Normalize notification type (strip App\Notifications\ prefix if present)
      let notificationType = notificationData.type;
      if (notificationType && notificationType.startsWith('App\\Notifications\\')) {
        notificationType = notificationType.replace('App\\Notifications\\', '');
        // Convert PascalCase to snake_case if needed (e.g. VideoUploaded -> video_uploaded)
        // ideally the backend sends correct type in data, but if we rely on class name:
        notificationType = notificationType.replace(/([A-Z])/g, "_$1").toLowerCase().replace(/^_/, "");
      }

      // Check for specific override in data payload
      if (notificationData.data && notificationData.data.type) {
        notificationType = notificationData.data.type;
      }

      console.log('🔔 Processing notification with type:', notificationType, 'Original:', notificationData.type);

      // Route to appropriate handler based on type
      switch (notificationType) {
        case 'appointment_reminder':
          showNotificationToast(notificationData, {
            type: 'info',
            title: notificationData.title || 'Appointment Reminder',
            message: notificationData.content || 'You have an upcoming appointment',
            image: notificationData.image || notificationData.sender_image,
            duration: 5000,
            action_url: notificationData.action_url
          });
          break;
        case 'guardianship':
        case 'guardianship_invitation':
        case 'guardianship_response':
        case 'guardianship_accepted':
        case 'guardianship_rejected': {
          console.log('🛡️ [useNotifications] Explicit Guardianship Match:', { notificationType, notificationData });
          updateNotificationBadgeCount();
          
          const petId = notificationData.pet_id || notificationData.data?.pet_id || notificationData.target_id;
          const status = notificationData.status || notificationData.data?.status || (notificationType.includes('accepted') ? 'accepted' : notificationType.includes('rejected') ? 'rejected' : null);
          const isInvitation = notificationType === 'guardianship_invitation' || notificationData.category === 'guardianship_invitation';

          if (isInvitation) {
            // Highlight the guardianship request in session storage before navigating
            const requestId = notificationData.data?.request_id || notificationData.target_id || notificationData.id;
            if (requestId) {
              sessionStorage.setItem('highlightGuardianshipRequestId', requestId.toString());
            }

            showNotificationToast(notificationData, {
              type: 'info',
              title: notificationData.title || 'Guardianship Invitation',
              message: notificationData.content || notificationData.message || 'You have been invited to become a guardian',
              image: notificationData.image || notificationData.sender_image,
              duration: 8000,
              buttons: [
                {
                  text: 'View',
                  onClick: () => {
                    navigate('/dashboard/pets');
                  },
                  primary: true
                }
              ]
            });
          } else {
            // Response/Update case
            showNotificationToast(notificationData, {
              type: status === 'accepted' ? 'success' : 'info',
              title: notificationData.title || 'Guardianship Update',
              message: notificationData.content || notificationData.message || 'Guardianship invitation update',
              image: notificationData.image || notificationData.sender_image,
              duration: 8000,
              onClick: () => {
                if (petId) {
                  navigate(`/pets/${petId}/view`);
                } else if (notificationData.action_url) {
                  const url = notificationData.action_url;
                  if (url.startsWith('http')) {
                    try {
                      const path = new URL(url).pathname;
                      navigate(path);
                    } catch (e) {
                      navigate(url);
                    }
                  } else {
                    navigate(url);
                  }
                }
              },
              buttons: [
                {
                  text: 'View',
                  onClick: () => {
                    if (petId) {
                      navigate(`/pets/${petId}/view`);
                    } else if (notificationData.action_url) {
                      const url = notificationData.action_url;
                      if (url.startsWith('http')) {
                        try {
                          const path = new URL(url).pathname;
                          navigate(path);
                        } catch (e) {
                          navigate(url);
                        }
                      } else {
                        navigate(url);
                      }
                    } else {
                      navigate('/dashboard/pets');
                    }
                  },
                  primary: true
                }
              ]
            });
          }
          break;
        }
        case 'new_reaction':
        case 'post_reaction':
        case 'story_reaction':
        case 'comment_reaction':
        case 'like':
          handleReactionNotification(notificationData);
          break;
        case 'messages_read':
          handleMessagesReadNotification(notificationData);
          break;
        case 'new_message_received':
          // Unified message notification handler - works on any page
          handleMessageReceived(notificationData);
          break;
        case 'new_message_received_in_medical_conversation':
          processMedicalMessageNotification(notificationData);
          break;
        case 'message_sent_from_my_side':
          handleMessageSentFromMySide(notificationData);
          break;
        case 'message_deleted':
          handleMessageDeleted(notificationData);
          break;
        case 'new_follow_request':
        case 'connection_request':
          handleFollowRequestNotification(notificationData);
          break;
        case 'connection_accepted':
          handleConnectionAcceptedNotification(notificationData);
          break;
        case 'clinic_invitation':
          handleClinicInvitationNotification(notificationData);
          break;
        case 'clinic_invitation_accepted':
          handleClinicInvitationAcceptedNotification(notificationData);
          break;
        case 'clinic_invitation_rejected':
          handleClinicInvitationRejectedNotification(notificationData);
          break;
        case 'channel_invitation':
          // Channel invitation notification - refresh channels when accepted
          showNotificationToast(notificationData, {
            type: 'info',
            title: notificationData.title || 'Channel Invitation',
            message: notificationData.content || notificationData.message || 'You have been invited to join a channel',
            image: notificationData.image || notificationData.sender_image,
            duration: 5000,
            action_url: notificationData.action_url
          });
          break;
        case 'channel_join_request':
        case 'channel_join_request_submitted':
          // Channel join request notification (for admins/owners)
          window.dispatchEvent(new CustomEvent('channel:join-request-submitted', {
            detail: {
              channelId: notificationData.target_id || notificationData.channel_id,
              notificationData
            }
          }));
          showNotificationToast(notificationData, {
            type: 'info',
            title: notificationData.title || 'Channel Join Request',
            message: notificationData.content || notificationData.message || 'Someone wants to join your channel',
            image: notificationData.image || notificationData.sender_image,
            duration: 5000,
            action_url: notificationData.action_url
          });
          break;

        case 'channel_join_request_accepted':
          // When a join request is accepted, refresh channels
          window.dispatchEvent(new CustomEvent('channel:join-request-accepted', {
            detail: {
              channelId: notificationData.target_id || notificationData.channel_id,
              notificationData
            }
          }));
          showNotificationToast(notificationData, {
            type: 'success',
            title: notificationData.title || 'Join Request Accepted',
            message: notificationData.content || notificationData.message || 'Your request to join the channel has been accepted',
            image: notificationData.image,
            duration: 5000,
            action_url: notificationData.action_url
          });
          break;
        case 'urgent_case_accepted_by_vet':
          handleUrgentCaseAcceptedByOtherNotification(notificationData);
          break;
        case 'urgent_medical_case_assignment':
          handleUrgentCaseNotification(notificationData);
          break;
        case 'urgent_medical_case':
          // Map target_id to case_id/content_id for the modal to find the case
          const mappedNotificationData = { ...notificationData };
          if (notificationData.target_type === 'urgentMedicalCaseClass' && notificationData.target_id) {
            mappedNotificationData.case_id = notificationData.target_id;
            mappedNotificationData.content_id = notificationData.target_id;
            mappedNotificationData.cid = notificationData.target_id;
          }
          handleUrgentCaseNotification(mappedNotificationData);
          break;
        case 'conversation_closed':
          handleConversationClosedNotification(notificationData);
          break;
        // Appointment notifications
        case 'appointment_created':
        case 'appointment_updated':
        case 'appointment_canceled':
        case 'appointment_restored':
        case 'appointment_status_changed':
          handleAppointmentNotification(notificationData);
          break;
        // Outpatient visit notifications
        case 'outpatient_visit_created':
        case 'outpatient_visit_updated':
        case 'outpatient_visit_status_changed':
          handleOutpatientVisitNotification(notificationData);
          break;
        // Inpatient visit notifications
        case 'inpatient_visit_created':
        case 'inpatient_visit_updated':
        case 'inpatient_visit_status_changed':
          handleInpatientVisitNotification(notificationData);
          break;

        case 'user_typing':
          // Typing notifications are handled in ChatPage - don't show toast
          // Dispatch event for ChatPage to handle
          window.dispatchEvent(new CustomEvent('notification-received', {
            detail: notificationData
          }));
          break;
        case 'user_stop_typing':
          // Typing notifications are handled in ChatPage - don't show toast
          // Dispatch event for ChatPage to handle
          window.dispatchEvent(new CustomEvent('user_stop_typing', {
            detail: notificationData
          }));
          break;
        case 'new_user_same_city': {
          // Handle new user same city notification
          updateNotificationBadgeCount();
          const newUserCityName = notificationData.city_name || 'your city';
          const newUserSenderName = notificationData.sender_name || notificationData.user_name || 'Someone';
          showNotificationToast(notificationData, {
            type: 'info',
            title: notificationData.title || 'New User in Your City',
            message: notificationData.content || notificationData.message || `${newUserSenderName} just joined Sulhafa from ${newUserCityName}`,
            image: notificationData.sender_image || notificationData.image,
            sender_name: newUserSenderName,
            sender_image: notificationData.sender_image || notificationData.image,
            duration: 8000,
            action_url: notificationData.action_url,
            buttons: [
              {
                text: 'View Profile',
                onClick: () => {
                  const userId = notificationData.user_id || notificationData.sender_id || notificationData.target_id;
                  if (userId) {
                    navigate(`/social/profile/${userId}`);
                  } else if (notificationData.action_url) {
                    navigate(notificationData.action_url);
                  }
                },
                primary: true
              }
            ]
          });
          break;
        }
        // Adoption notifications
        case 'adoption_request_received':
        case 'AdoptionRequestReceived':
        case 'adoptionRequestReceived':
          handleAdoptionRequestReceivedNotification(notificationData);
          break;
        case 'adoption_request_accepted':
        case 'AdoptionRequestAccepted':
        case 'adoptionRequestAccepted':
          handleAdoptionRequestAcceptedNotification(notificationData);
          break;
        case 'adoption_request_rejected':
        case 'AdoptionRequestRejected':
        case 'adoptionRequestRejected':
          handleAdoptionRequestRejectedNotification(notificationData);
          break;
        case 'new_post':
          handleNewPostNotification(notificationData);
          break;
        case 'comment':
          handleCommentNotification(notificationData);
          break;
        case 'urgent_medical_case_response':
        case 'health_reminder':
          handleStandardNotification(notificationData);
          break;
        case 'video_uploaded':
          console.log('📤 Video uploaded:', notificationData);
          showNotificationToast(notificationData, {
            type: 'info',
            title: notificationData.title || (notificationData.data ? notificationData.data.title : null) || 'Video Uploaded',
            message: notificationData.message || (notificationData.data ? notificationData.data.message : null) || 'Processing started...',
            duration: 5000
          });
          break;
        case 'video_processing_completed':
          handleVideoProcessingCompleted(notificationData);
          break;
        case 'post_deleted_by_admin':
          setPostDeletedModalData({
            reasons: notificationData?.data?.reasons || notificationData?.reasons || null,
            reason: notificationData?.data?.reason || notificationData?.reason || null,
            reason_labels: notificationData?.data?.reason_labels || notificationData?.reason_labels || null,
          });
          setPostDeletedModalOpen(true);
          showNotificationToast(notificationData, {
            type: 'warning',
            title: notificationData.title || notificationData?.data?.title || 'Post Deleted',
            message: notificationData.message || notificationData?.data?.message || 'Your post has been deleted by our moderation team.',
            duration: 8000,
            onClick: () => {
              // Re-open modal when toast is clicked
              setPostDeletedModalData({
                reasons: notificationData?.data?.reasons || notificationData?.reasons || null,
                reason: notificationData?.data?.reason || notificationData?.reason || null,
                reason_labels: notificationData?.data?.reason_labels || notificationData?.reason_labels || null,
              });
              setPostDeletedModalOpen(true);
            },
          });
          break;
        default:
          // Handle all other notification types with standardized structure
          console.log('🔔 [useNotifications] Default notification handler for type:', notificationType);

          // Check if this might be an adoption notification with different type name
          if (notificationData.type && (
            notificationData.type.includes('adoption') ||
            notificationData.type.includes('Adoption') ||
            notificationData.adoption_request_id ||
            notificationData.adoption_post_id
          )) {
            // Adoption handled
          }
          
          // Special fallback for guardianship category if type didn't match
          if (notificationData.category === 'guardianship' && notificationType !== 'guardianship_response' && notificationType !== 'guardianship_invitation') {
             console.log('🛡️ [useNotifications] Fallback guardianship handling via category');
             // We can manually trigger the same logic if needed, but the switch should have caught it if it matched one of the cases
          }

          showNotificationToast(notificationData, {
            type: notificationData.priority === 'high' ? 'info' : 'info',
            title: notificationData.title || 'New Notification',
            message: notificationData.content || notificationData.message || 'You have a new notification',
            image: notificationData.image || notificationData.sender_image,
            sender_name: notificationData.sender_name,
            sender_image: notificationData.sender_image,
            duration: notificationData.priority === 'high' ? 8000 : 5000,
            action_url: notificationData.action_url,
            buttons: notificationData.action_url ? [
              {
                text: 'View',
                onClick: () => {
                  if (notificationData.action_url) {
                    navigate(notificationData.action_url);
                  }
                },
                primary: true
              }
            ] : []
          });
      }
    } catch (error) {
      console.error('❌ Error parsing notification data:', error);
    }
  };

  const handleConversationClosedNotification = (notificationData) => {
    try {

      // Extract relevant information from standardized notification structure
      const contentId = notificationData.target_id || notificationData.content_id;
      const message = notificationData.content || notificationData.message;

      // Validate required fields
      if (!contentId) {
        console.error('❌ Missing content_id in conversation closed notification');
        return;
      }

      // Check if user is currently on the chat page (including doctor chat)
      const currentPath = window.location.pathname;
      const isOnChatPage = currentPath.includes('/chat') || currentPath.includes('/medical/clinic/chat');


      if (isOnChatPage) {
        // User is on chat page - handle conversation closure in chat

        // IMMEDIATELY dispatch a custom event to notify ChatPage about the conversation closure
        const conversationClosedEvent = new CustomEvent('conversationClosed', {
          detail: {
            conversationId: contentId,
            message: message,
            timestamp: new Date().toISOString()
          }
        });
        // Dispatch the event IMMEDIATELY for instant UI update
        window.dispatchEvent(conversationClosedEvent);


      } else {
        // User is not on chat page - show toast notification

        showNotificationToast(notificationData, {
          type: 'info',
          title: notificationData.title || 'Conversation Closed',
          message: message || notificationData.content || 'The conversation has been closed',
          image: notificationData.sender_image,
          duration: 5000
        });
      }

    } catch (error) {
      console.error('❌ Error handling conversation closed notification:', error);
    }
  };
  // Handle urgent case notifications
  const handleUrgentCaseNotification = (notificationData) => {
    try {
      // Validate notification data to prevent errors
      if (!notificationData) {
        console.error('❌ No notification data provided');
        return;
      }


      // Set the notification data and open the modal
      // Note: urgent case data may have additional nested structure in the body field
      // but we'll use the standardized fields first
      setUrgentCaseData(notificationData);
      setUrgentCaseModalOpen(true);


    } catch (error) {
      console.error('❌ Error handling urgent case notification:', error);
      // Don't let errors crash the app
    }
  };

  // Handle urgent case accepted by other doctor notification
  const handleUrgentCaseAcceptedByOtherNotification = (notificationData) => {
    try {

      // Parse nested data if it exists
      let actualNotificationData = notificationData;
      if (notificationData.data && typeof notificationData.data === 'string' && notificationData.data !== 'undefined') {
        try {
          actualNotificationData = JSON.parse(notificationData.data);
        } catch (error) {
          console.error('❌ Error parsing nested accepted by other data:', error);
        }
      }

      // Map target_id to case_id/content_id/cid for the modal to find the case
      if (notificationData.target_type === 'UrgentMedicalCase' && notificationData.target_id) {
        actualNotificationData.case_id = notificationData.target_id;
        actualNotificationData.content_id = notificationData.target_id;
        actualNotificationData.cid = notificationData.target_id;
      }

      // Get current user ID to check if this notification is for the current user
      const userData = JSON.parse(localStorage.getItem('user_data') || '{}');
      const currentUserId = userData.id || userData.user_id;
      const senderId = actualNotificationData.sender_id || notificationData.sender_id;


      // If this notification is for the current user (Pet Guardian), show the accepted modal
      if (currentUserId && senderId && currentUserId.toString() === senderId.toString()) {

        // Create the accepted modal data
        const acceptedModalData = {
          ...actualNotificationData,
          ...notificationData, // Include original notification data to preserve all fields
          accepted_by_other: notificationData.accepted_by_other !== undefined ? notificationData.accepted_by_other : true,
          is_accepted_modal: notificationData.is_accepted_modal !== undefined ? notificationData.is_accepted_modal : true, // Flag to identify this as the accepted modal
          message: actualNotificationData.problem_description || notificationData.message || 'Urgent case notification',
          sender: notificationData.sender || actualNotificationData.sender || { name: 'Pet Guardian' },
          pet_data: notificationData.pet_data || (actualNotificationData.pet_names ? (typeof actualNotificationData.pet_names === 'string' ? JSON.parse(actualNotificationData.pet_names).map(name => ({ name })) : actualNotificationData.pet_names) : [])
        };

        setUrgentCaseData(acceptedModalData);
        setUrgentCaseModalOpen(true);
        return;
      }

      // Check if the urgent case modal is currently open for the same case (for doctors)

      if (urgentCaseModalOpen && urgentCaseData) {
        const currentCaseId = urgentCaseData.case_id || urgentCaseData.content_id || urgentCaseData.cid || urgentCaseData.target_id;
        const newCaseId = actualNotificationData.case_id || actualNotificationData.content_id || actualNotificationData.cid || notificationData.target_id;


        if (currentCaseId && newCaseId && currentCaseId.toString() === newCaseId.toString()) {

          // Update the urgent case data to include the accepted status
          const updatedData = {
            ...urgentCaseData,
            ...actualNotificationData,
            ...notificationData, // Include original notification data
            accepted_by_other: notificationData.accepted_by_other !== undefined ? notificationData.accepted_by_other : true,
            accepted_by_doctor_name: actualNotificationData.accepted_by_doctor_name || notificationData.accepted_by_doctor_name,
            accepted_at: actualNotificationData.accepted_at || notificationData.accepted_at
          };

          setUrgentCaseData(updatedData);
        } else {
        }
      } else {

        // Open the modal with the accepted by other data
        const acceptedData = {
          ...actualNotificationData,
          ...notificationData, // Include original notification data to preserve all fields
          accepted_by_other: notificationData.accepted_by_other !== undefined ? notificationData.accepted_by_other : true,
          // Add some default fields that might be missing
          message: actualNotificationData.problem_description || notificationData.message || 'Urgent case notification',
          sender: notificationData.sender || actualNotificationData.sender || { name: 'Pet Guardian' },
          pet_data: notificationData.pet_data || (actualNotificationData.pet_names ? (typeof actualNotificationData.pet_names === 'string' ? JSON.parse(actualNotificationData.pet_names).map(name => ({ name })) : actualNotificationData.pet_names) : [])
        };

        setUrgentCaseData(acceptedData);
        setUrgentCaseModalOpen(true);
      }
    } catch (error) {
      console.error('❌ Error handling urgent case accepted by other notification:', error);
    }
  };

  // Handle reaction notifications
  const handleReactionNotification = (notificationData) => {
    try {

      // Extract data from standardized structure
      const nestedData = getNestedNotificationData(notificationData);
      const reactionType = notificationData.reaction_type || nestedData.reaction_type || 'like';
      const contentId = notificationData.target_id || notificationData.content_id || nestedData.target_id || nestedData.content_id;
      const actionUrl = notificationData.action_url || nestedData.action_url;
      const senderName = notificationData.sender_name || nestedData.sender_name || 'Someone';
      const senderImage = notificationData.sender_image || notificationData.image || nestedData.sender_image || nestedData.image;

      // Get reaction label
      const getReactionLabel = (type) => {
        const labelMap = {
          'like': 'was confused about',
          'love': 'loved',
          'haha': 'laughed at',
          'wow': 'was amazed by',
          'sad': 'felt sad about',
          'angry': 'was angry about'
        };
        return labelMap[type] || 'reacted to';
      };

      const reactionLabel = getReactionLabel(reactionType);
      const contentType = notificationData.type === 'story_reaction' || (nestedData.content_type && nestedData.content_type.includes('Story'))
        ? 'story'
        : 'post';

      // Show notification toast with reaction details
      showNotificationToast(notificationData, {
        type: 'info',
        title: `${senderName} reacted`,
        message: notificationData.content || `${senderName} ${reactionLabel} your ${contentType}`,
        image: senderImage,
        sender_name: senderName,
        sender_image: senderImage,
        duration: 8000,
        action_url: actionUrl,
        buttons: contentType === 'story'
          ? []
          : [
            {
              text: 'View Post',
              onClick: () => {
                // Try to open modal first if we have a content ID
                if (contentId) {
                  openPostModal(contentId);
                } else if (actionUrl) {
                  // Handle absolute vs relative URLs
                  if (actionUrl.startsWith('http://') || actionUrl.startsWith('https://')) {
                    window.location.href = actionUrl;
                  } else {
                    navigate(actionUrl);
                  }
                } else {
                  navigate('/social');
                }
              },
              primary: true
            }
          ]
      });

      // Dispatch custom event for reaction updates
      const reactionUpdateEvent = new CustomEvent('reaction-notification-received', {
        detail: {
          type: 'reaction',
          reactionType: reactionType,
          contentId: contentId,
          sender: {
            name: senderName,
            image: senderImage
          },
          message: notificationData.content,
          timestamp: new Date().toISOString()
        }
      });
      window.dispatchEvent(reactionUpdateEvent);

    } catch (error) {
      console.error('❌ Error handling reaction notification:', error);
      // Fallback to basic notification
      showNotificationToast(notificationData, {
        type: 'info',
        title: notificationData.title || 'New Reaction',
        message: notificationData.content || 'Someone reacted to your post',
        duration: 5000
      });
    }
  };

  const getNestedNotificationData = (notificationData) => {
    if (!notificationData?.data) {
      return {};
    }

    if (typeof notificationData.data === 'string') {
      try {
        return JSON.parse(notificationData.data);
      } catch (error) {
        return {};
      }
    }

    return notificationData.data;
  };

  const handleCommentNotification = (notificationData) => {
    try {
      const nestedData = getNestedNotificationData(notificationData);
      let bodyData = {};
      if (notificationData.body && typeof notificationData.body === 'string') {
        try {
          bodyData = JSON.parse(notificationData.body);
        } catch (error) {
          bodyData = {};
        }
      } else if (notificationData.body && typeof notificationData.body === 'object') {
        bodyData = notificationData.body;
      }

      const sender = notificationData.sender || nestedData.sender || bodyData.sender || {};
      const senderName =
        notificationData.sender_name ||
        notificationData.user_name ||
        sender.name ||
        'Someone';
      const senderImage =
        notificationData.sender_image ||
        notificationData.image ||
        sender.image ||
        null;
      const postId =
        notificationData.post_id ||
        nestedData.post_id ||
        bodyData.post_id ||
        notificationData.target_id;
      const commentText =
        notificationData.comment_text ||
        nestedData.comment_text ||
        bodyData.comment_text;

      showNotificationToast(notificationData, {
        type: 'info',
        title: notificationData.title || 'New Comment',
        message: notificationData.message || `${senderName} commented on your post`,
        image: senderImage,
        sender_name: senderName,
        sender_image: senderImage,
        duration: 8000,
        buttons: postId ? [
          {
            text: 'View Post',
            onClick: () => openPostModal(postId),
            primary: true
          }
        ] : []
      });

      if (commentText) {
        window.dispatchEvent(new CustomEvent('post-comment-notification-received', {
          detail: {
            post_id: postId,
            comment_text: commentText,
            sender_name: senderName
          }
        }));
      }
    } catch (error) {
      console.error('❌ Error handling comment notification:', error);
    }
  };

  const handleStandardNotification = (notificationData) => {
    try {
      const nestedData = getNestedNotificationData(notificationData);

      showNotificationToast(notificationData, {
        type: notificationData.priority === 'urgent' || notificationData.priority === 'high' ? 'warning' : 'info',
        title: notificationData.title || nestedData.title || 'Notification',
        message: notificationData.message || notificationData.content || nestedData.message || nestedData.content || 'You have a new notification',
        image: notificationData.sender_image || notificationData.image || nestedData.sender_image || nestedData.image,
        sender_name: notificationData.sender_name || notificationData.user_name || nestedData.sender_name,
        sender_image: notificationData.sender_image || notificationData.image || nestedData.sender_image || nestedData.image,
        duration: 8000,
        action_url: notificationData.action_url || nestedData.action_url
      });
    } catch (error) {
      console.error('❌ Error handling standard notification:', error);
    }
  };

  const handleFollowRequestNotification = (notificationData) => {
    try {

      // Update notification badge count
      updateNotificationBadgeCount();

      // Use standardized notification structure
      showNotificationToast(notificationData, {
        type: 'info',
        title: notificationData.title || notificationData.sender_name || 'New Buddy Request',
        message: notificationData.content || notificationData.message || 'Someone sent you a buddy request',
        image: notificationData.sender_image || notificationData.image,
        sender_name: notificationData.sender_name,
        sender_image: notificationData.sender_image,
        duration: 5000,
        action_url: notificationData.action_url,
        buttons: [
          {
            text: 'View Follow Requests',
            onClick: () => {
              if (notificationData.action_url) {
                navigate(notificationData.action_url);
              } else {
                navigate('/notifications');
              }
            },
            primary: true
          }
        ]
      });
    } catch (error) {
      console.error('❌ Error handling follow request notification:', error);
      // Fallback notification
      showNotificationToast(notificationData, {
        type: 'info',
        title: notificationData.title || 'New Buddy Request',
        message: notificationData.content || 'Someone wants to be buddies with you',
        duration: 5000
      });
    }
  };

  const handleConnectionAcceptedNotification = (notificationData) => {
    try {

      // Extract sender information
      const senderId = notificationData.sender_id || notificationData.user_id;
      const senderName = notificationData.sender_name || notificationData.user_name || 'Someone';
      const senderImage = notificationData.sender_image || notificationData.image;

      // Use standardized notification structure
      showNotificationToast(notificationData, {
        type: 'success',
        title: notificationData.title || 'Buddy Request Accepted',
        message: notificationData.content || notificationData.message || `${senderName} accepted your buddy request`,
        image: senderImage,
        sender_name: senderName,
        sender_image: senderImage,
        duration: 5000,
        action_url: notificationData.action_url,
        buttons: [
          {
            text: 'View Profile',
            onClick: () => {
              navigate(`/social/profile/${senderId}`);
            },
            primary: true
          }
        ]
      });

      // Dispatch custom event for UI updates
      const connectionAcceptedEvent = new CustomEvent('connection-accepted', {
        detail: {
          type: 'connection_accepted',
          senderId: senderId,
          sender: {
            id: senderId,
            name: senderName,
            image: senderImage
          },
          message: notificationData.content || notificationData.message,
          timestamp: new Date().toISOString()
        }
      });
      window.dispatchEvent(connectionAcceptedEvent);
    } catch (error) {
      console.error('❌ Error handling connection accepted notification:', error);
      // Fallback notification
      showNotificationToast(notificationData, {
        type: 'success',
        title: notificationData.title || 'Buddy Request Accepted',
        message: notificationData.content || 'Your buddy request has been accepted',
        duration: 5000
      });
    }
  };

  // Handle clinic invitation notifications
  const handleClinicInvitationNotification = (notificationData) => {
    try {

      // Use standardized structure, but also check for nested data if needed
      const invitationId = notificationData.invitation_id || notificationData.target_id;
      const clinicName = notificationData.clinic_name || 'a clinic';

      showNotificationToast(notificationData, {
        type: 'info',
        title: notificationData.title || 'Clinic Invitation',
        message: notificationData.content || `You have been invited to join ${clinicName}`,
        image: notificationData.sender_image || notificationData.image,
        duration: 0, // Don't auto-dismiss since we have action buttons
        buttons: [
          {
            text: 'Accept',
            onClick: async () => {
              try {
                const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";
                const token = localStorage.getItem("access_token");

                const response = await fetch(`${API_BASE_URL}/api/v1/clinic-members/${invitationId}/accept`, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                  }
                });

                if (response.ok) {
                  showNotificationToast({
                    title: "Invitation Accepted",
                    body: `You have successfully joined ${clinicName}!`
                  }, {
                    type: 'success',
                    duration: 4000
                  });
                } else {
                  const errorData = await response.json();
                  showNotificationToast({
                    title: "Accept Failed",
                    body: errorData.message || "Failed to accept invitation"
                  }, {
                    type: 'error',
                    duration: 5000
                  });
                }
              } catch (error) {
                console.error('Error accepting invitation:', error);
                showNotificationToast({
                  title: "Accept Failed",
                  body: "Failed to accept invitation"
                }, {
                  type: 'error',
                  duration: 5000
                });
              }
            },
            success: true
          },
          {
            text: 'Reject',
            onClick: async () => {
              try {
                const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";
                const token = localStorage.getItem("access_token");

                const response = await fetch(`${API_BASE_URL}/api/v1/clinic-members/${invitationId}/reject`, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                  }
                });

                if (response.ok) {
                  showNotificationToast({
                    title: "Invitation Declined",
                    body: `You have declined the invitation from ${clinicName}`
                  }, {
                    type: 'warning',
                    duration: 4000
                  });
                } else {
                  const errorData = await response.json();
                  showNotificationToast({
                    title: "Reject Failed",
                    body: errorData.message || "Failed to reject invitation"
                  }, {
                    type: 'error',
                    duration: 5000
                  });
                }
              } catch (error) {
                console.error('Error rejecting invitation:', error);
                showNotificationToast({
                  title: "Reject Failed",
                  body: "Failed to reject invitation"
                }, {
                  type: 'error',
                  duration: 5000
                });
              }
            },
            danger: true
          },
          {
            text: 'View Details',
            onClick: () => {
              window.location.href = '/medical/manage-team';
            },
            primary: true
          }
        ]
      });
    } catch (error) {
      console.error('❌ Error handling clinic invitation notification:', error);
      showNotificationToast(notificationData, {
        type: 'info',
        title: notificationData.title || 'Clinic Invitation',
        message: notificationData.content || 'You have received a clinic invitation',
        duration: 5000
      });
    }
  };

  // Handle clinic invitation accepted notifications
  const handleClinicInvitationAcceptedNotification = (notificationData) => {
    try {

      const clinicName = notificationData.clinic_name || 'the clinic';
      const memberName = notificationData.member_name || notificationData.sender_name || 'A member';

      showNotificationToast(notificationData, {
        type: 'success',
        title: notificationData.title || 'Clinic Invitation Accepted',
        message: notificationData.content || `${memberName} has accepted your invitation to join ${clinicName}`,
        image: notificationData.sender_image || notificationData.image,
        duration: 5000
      });
    } catch (error) {
      console.error('❌ Error handling clinic invitation accepted notification:', error);
      showNotificationToast(notificationData, {
        type: 'success',
        title: notificationData.title || 'Clinic Invitation Accepted',
        message: notificationData.content || 'Someone has accepted your clinic invitation',
        duration: 5000
      });
    }
  };

  // Handle clinic invitation rejected notifications
  const handleClinicInvitationRejectedNotification = (notificationData) => {
    try {

      const clinicName = notificationData.clinic_name || 'the clinic';
      const memberName = notificationData.member_name || notificationData.sender_name || 'A member';

      showNotificationToast(notificationData, {
        type: 'warning',
        title: notificationData.title || 'Clinic Invitation Declined',
        message: notificationData.content || `${memberName} has declined your invitation to join ${clinicName}`,
        image: notificationData.sender_image || notificationData.image,
        duration: 6000,
        buttons: [
          {
            text: 'View Team',
            onClick: () => {
              window.location.href = '/medical/manage-team';
            },
            primary: true
          }
        ]
      });
    } catch (error) {
      console.error('❌ Error handling clinic invitation rejected notification:', error);
      showNotificationToast({
        title: "Clinic Invitation Declined",
        body: "Someone has declined your clinic invitation"
      }, {
        type: 'warning',
        duration: 5000
      });
    }
  };

  // // Function to send token to backend
  // const sendTokenToServer = async (token) => {
  //   try {

  //     // Get the current user's access token
  //     const accessToken = localStorage.getItem('access_token');
  //     if (!accessToken) {
  //       return false;
  //     }

  //     if (!token || !isValidFcmToken(token)) {
  //       return false;
  //     }

  //     const requestBody = {
  //       fcm: token,
  //       device_type: 'web'
  //     };

  //     // Send token to your backend API using the specified endpoint
  //     const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:8000'}/api/v1/fcm/save`, {
  //       method: 'POST',
  //       headers: {
  //         'Content-Type': 'application/json',
  //         'Authorization': `Bearer ${accessToken}`
  //       },
  //       body: JSON.stringify(requestBody)
  //     });


  //     if (response.ok) {
  //       const responseData = await response.json();
  //       localStorage.setItem('fcmToken', token);
  //       return true;
  //     } else {
  //       const errorData = await response.json().catch(() => ({}));
  //       console.error('❌ Failed to send FCM token to server:', response.status, errorData);
  //       return false;
  //     }
  //   } catch (error) {
  //     console.error('❌ Error sending FCM token to server:', error);
  //     return false;
  //   }
  // };

  // Function to manually request permission
  // Request permission wrapper (duplicate removed - using the one defined earlier)




  // Update reaction badge count in navbar
  const updateReactionBadgeCount = () => {
    try {
      // Get current reaction count from localStorage
      const currentCount = parseInt(localStorage.getItem('reaction_badge_count') || '0');
      const newCount = currentCount + 1;

      // Store updated count
      localStorage.setItem('reaction_badge_count', newCount.toString());

      // Dispatch event to update navbar badge
      const badgeUpdateEvent = new CustomEvent('reaction-badge-update', {
        detail: {
          count: newCount,
          type: 'reaction',
          timestamp: new Date().toISOString()
        }
      });
      window.dispatchEvent(badgeUpdateEvent);

    } catch (error) {
      console.error('❌ Error updating reaction badge count:', error);
    }
  };

  // Update notification badge count in navbar
  const updateNotificationBadgeCount = () => {
    try {
      // Get current notification count from localStorage
      const currentCount = parseInt(localStorage.getItem('notification_badge_count') || '0');
      const newCount = currentCount + 1;

      // Store updated count
      localStorage.setItem('notification_badge_count', newCount.toString());

      // Dispatch event to update navbar badge
      const badgeUpdateEvent = new CustomEvent('notification-badge-update', {
        detail: {
          count: newCount,
          type: 'notification',
          timestamp: new Date().toISOString()
        }
      });
      window.dispatchEvent(badgeUpdateEvent);

    } catch (error) {
      console.error('❌ Error updating notification badge count:', error);
    }
  };


  // Modal control functions
  const closeUrgentCaseModal = () => {
    setUrgentCaseModalOpen(false);
    setUrgentCaseData(null);
  };

  const closeTelemedicineModal = () => {
    setTelemedicineModalOpen(false);
    setTelemedicineData(null);
  };

  // Appointment notification handlers
  const handleAppointmentNotification = async (notificationData) => {
    try {

      // Extract appointment ID
      const appointmentId = notificationData?.appointment_id || notificationData?.target_id;

      // Get notification details
      const title = notificationData?.title || 'Appointment Notification';

      // Try to fetch appointment details if we have an appointment ID
      let appointmentDetails = null;
      let petName = '';
      let clinicName = '';
      let appointmentDate = '';
      let vetName = '';

      if (appointmentId) {
        try {
          const response = await clinicAppointmentsAPI.getAppointment(appointmentId);
          appointmentDetails = response.data?.data || response.data;

          // Extract details from fetched appointment
          if (appointmentDetails) {
            petName = appointmentDetails.pet?.name || 'Unknown Pet';
            clinicName = appointmentDetails.clinic?.name || 'Unknown Clinic';
            appointmentDate = appointmentDetails.appointment_at || '';
            vetName = appointmentDetails.vet?.user?.name ||
              appointmentDetails.vet?.full_name ||
              appointmentDetails.vet?.name ||
              'Not assigned';
          }
        } catch (error) {
          console.warn('⚠️ Could not fetch appointment details, using notification content:', error);
          // Fallback: try to parse from notification content
          const content = notificationData?.content || '';
          // Parse content like "A new appointment has been created for Buddy at Sulhafa Veterinary Clinic on 2025-11-09 21:00."
          const petMatch = content.match(/for\s+([^at]+?)\s+at/i);
          const clinicMatch = content.match(/at\s+([^on]+?)\s+on/i);
          const dateMatch = content.match(/on\s+([^.]+)/i);

          if (petMatch) petName = petMatch[1].trim();
          if (clinicMatch) clinicName = clinicMatch[1].trim();
          if (dateMatch) appointmentDate = dateMatch[1].trim();
        }
      } else {
        // No appointment ID, try to extract from notification content
        const content = notificationData?.content || '';
        const petMatch = content.match(/for\s+([^at]+?)\s+at/i);
        const clinicMatch = content.match(/at\s+([^on]+?)\s+on/i);
        const dateMatch = content.match(/on\s+([^.]+)/i);

        if (petMatch) petName = petMatch[1].trim();
        if (clinicMatch) clinicName = clinicMatch[1].trim();
        if (dateMatch) appointmentDate = dateMatch[1].trim();
      }

      // Format date if available
      let formattedDate = '';
      if (appointmentDate) {
        try {
          const date = new Date(appointmentDate);
          if (!isNaN(date.getTime())) {
            formattedDate = date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
          } else {
            formattedDate = appointmentDate;
          }
        } catch (e) {
          formattedDate = appointmentDate;
        }
      }

      // Create detailed message with appointment summary
      const summaryMessage = `New appointment on ${formattedDate}`;

      // Show toast notification with click handler to navigate to appointment
      // Use centralized routing utility for proper user-type-based routing
      showNotificationToast(notificationData, {
        type: 'info',
        title: title,
        message: summaryMessage,
        duration: 8000, // Show for 8 seconds
        onClick: () => {
          // Create a notification object with the appointment data
          const notificationForRouting = {
            type: 'appointment_created',
            parsedData: {
              appointment_id: appointmentId,
              ...notificationData
            },
            target_id: appointmentId,
            ...notificationData
          };

          // Use centralized routing which handles user types correctly
          handleNotificationRouting(
            notificationForRouting,
            navigate, // Use React Router navigate
            null, // onClose
            null  // openPostModal
          );
        },
        customStyle: {
          cursor: 'pointer',
        }
      });

    } catch (error) {
      console.error('❌ Error handling appointment notification:', error);
      // Show a basic notification even if there's an error
      showNotificationToast(notificationData, {
        type: 'info',
        title: notificationData?.title || 'Appointment Notification',
        message: notificationData?.content || 'You have a new appointment notification',
        duration: 8000,
        onClick: () => {
          const notificationForRouting = {
            type: 'appointment_created',
            parsedData: {
              appointment_id: appointmentId,
              ...notificationData
            },
            target_id: appointmentId,
            ...notificationData
          };

          handleNotificationRouting(
            notificationForRouting,
            navigate,
            null,
            null
          );
        },
        customStyle: {
          cursor: 'pointer',
        }
      });
    }
  };

  const closeAppointmentModal = () => {
    setAppointmentModalOpen(false);
    setAppointmentModalData(null);
  };

  // Outpatient visit notification handlers
  const handleOutpatientVisitNotification = async (notificationData) => {
    try {

      // Extract visit ID
      const visitId = notificationData?.outpatient_visit_id || notificationData?.visit_id || notificationData?.target_id;

      // Get notification details
      const title = notificationData?.title || 'Outpatient Visit Notification';

      // Try to fetch visit details if we have a visit ID
      let visitDetails = null;
      let petName = '';
      let clinicName = '';
      let visitDate = '';
      let vetName = '';
      let serialNumber = '';

      if (visitId) {
        try {
          const response = await outpatientAPI.getOutpatientVisit(visitId);
          visitDetails = response.data?.data || response.data;

          // Extract details from fetched visit
          if (visitDetails) {
            petName = visitDetails.pet?.name || 'Unknown Pet';
            clinicName = visitDetails.clinic?.name_en || visitDetails.clinic?.name || 'Unknown Clinic';
            visitDate = visitDetails.visit_date || '';
            vetName = visitDetails.vet?.user?.name ||
              visitDetails.vet?.full_name ||
              visitDetails.vet?.name ||
              'Not assigned';
            serialNumber = visitDetails.out_serial_num || '';
          }
        } catch (error) {
          console.warn('⚠️ Could not fetch outpatient visit details, using notification content:', error);
          // Fallback: try to parse from notification content
          const content = notificationData?.content || '';
          // Parse content like "A new outpatient visit has been created for Buddy at Sulhafa Veterinary Clinic (Serial: OUT-001)."
          const petMatch = content.match(/for\s+([^at]+?)\s+at/i);
          const clinicMatch = content.match(/at\s+([^(]+?)\s*\(/i);
          const serialMatch = content.match(/Serial:\s*([^)]+)/i);

          if (petMatch) petName = petMatch[1].trim();
          if (clinicMatch) clinicName = clinicMatch[1].trim();
          if (serialMatch) serialNumber = serialMatch[1].trim();
        }
      } else {
        // No visit ID, try to extract from notification content
        const content = notificationData?.content || '';
        const petMatch = content.match(/for\s+([^at]+?)\s+at/i);
        const clinicMatch = content.match(/at\s+([^(]+?)\s*\(/i);
        const serialMatch = content.match(/Serial:\s*([^)]+)/i);

        if (petMatch) petName = petMatch[1].trim();
        if (clinicMatch) clinicName = clinicMatch[1].trim();
        if (serialMatch) serialNumber = serialMatch[1].trim();
      }

      // Format date if available
      let formattedDate = '';
      if (visitDate) {
        try {
          const date = new Date(visitDate);
          if (!isNaN(date.getTime())) {
            formattedDate = date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
          } else {
            formattedDate = visitDate;
          }
        } catch (e) {
          formattedDate = visitDate;
        }
      }

      // Create detailed message with visit summary
      const summaryMessage = serialNumber
        ? `New outpatient visit\nSerial: ${serialNumber}\n${formattedDate ? formattedDate : ''}`
        : `New outpatient visit\n${formattedDate ? formattedDate : ''}`;

      // Show toast notification with click handler to navigate to visit
      showNotificationToast(notificationData, {
        type: 'info',
        title: title,
        message: summaryMessage,
        duration: 8000, // Show for 8 seconds
        onClick: () => {
          // Navigate to outpatient visit page with visit ID for highlighting
          if (visitId) {
            // Store visit ID in sessionStorage for highlighting
            sessionStorage.setItem('highlightOutpatientVisitId', visitId.toString());
            // Navigate to the outpatient tab in reception page
            const clinicId = getClinicId();
            const path = clinicId
              ? `/medical/clinic/${clinicId}/appointment-management/outpatient`
              : '/medical/clinic/appointment-management/outpatient';
            window.location.href = path;
          } else if (notificationData?.action_url) {
            window.location.href = notificationData.action_url;
          } else {
            // Fallback to outpatient list
            const clinicId = getClinicId();
            const path = clinicId
              ? `/medical/clinic/${clinicId}/appointment-management/outpatient`
              : '/medical/clinic/appointment-management/outpatient';
            window.location.href = path;
          }
        },
        customStyle: {
          cursor: 'pointer',
        }
      });

    } catch (error) {
      console.error('❌ Error handling outpatient visit notification:', error);
      // Show a basic notification even if there's an error
      showNotificationToast(notificationData, {
        type: 'info',
        title: notificationData?.title || 'Outpatient Visit Notification',
        message: notificationData?.content || 'You have a new outpatient visit notification',
        duration: 8000,
        onClick: () => {
          if (notificationData?.action_url) {
            window.location.href = notificationData.action_url;
          } else {
            const clinicId = getClinicId();
            const path = clinicId
              ? `/medical/clinic/${clinicId}/appointment-management/outpatient`
              : '/medical/clinic/appointment-management/outpatient';
            window.location.href = path;
          }
        },
        customStyle: {
          cursor: 'pointer',
        }
      });
    }
  };

  const closeOutpatientModal = () => {
    setOutpatientModalOpen(false);
    setOutpatientModalData(null);
  };

  // Inpatient visit notification handlers
  const handleInpatientVisitNotification = (notificationData) => {
    try {
      setInpatientModalData(notificationData);
      setInpatientModalOpen(true);
    } catch (error) {
      console.error('❌ Error handling inpatient visit notification:', error);
    }
  };

  const closeInpatientModal = () => {
    setInpatientModalOpen(false);
    setInpatientModalData(null);
  };

  // Adoption notification handlers
  const handleAdoptionRequestReceivedNotification = (notificationData) => {
    try {
      console.log('🐾 Handling adoption request received notification:', notificationData);

      // Extract request ID from notification data
      const requestId = notificationData?.adoption_request_id ||
        notificationData?.request_id ||
        notificationData?.target_id;

      const requesterName = notificationData?.sender_name ||
        notificationData?.requester_name ||
        'Someone';

      const petName = notificationData?.pet_name || 'a pet';

      // Show toast notification with button to view request
      showNotificationToast(notificationData, {
        type: 'info',
        title: notificationData?.title || 'New Adoption Request',
        message: notificationData?.content || notificationData?.message || `${requesterName} wants to adopt ${petName}`,
        image: notificationData?.sender_image || notificationData?.image,
        sender_name: requesterName,
        sender_image: notificationData?.sender_image || notificationData?.image,
        duration: 8000,
        buttons: [
          {
            text: 'View Request',
            onClick: () => {
              // Navigate to adoption page with modal open and request highlighted
              if (requestId) {
                navigate(`/adoption?openRequests=true&highlightRequest=${requestId}`);
              } else {
                navigate('/adoption?openRequests=true');
              }
            },
            primary: true
          }
        ]
      });

      updateNotificationBadgeCount();
    } catch (error) {
      console.error('❌ Error handling adoption request received notification:', error);
    }
  };

  const handleAdoptionRequestAcceptedNotification = (notificationData) => {
    try {
      console.log('✅ Handling adoption request accepted notification:', notificationData);

      const petName = notificationData?.pet_name || 'the pet';
      const authorName = notificationData?.sender_name || notificationData?.author_name || 'The owner';

      // Show success toast notification
      showNotificationToast(notificationData, {
        type: 'success',
        title: notificationData?.title || 'Adoption Request Accepted!',
        message: notificationData?.content || notificationData?.message || `${authorName} has accepted your request to adopt ${petName}`,
        image: notificationData?.sender_image || notificationData?.image,
        sender_name: authorName,
        sender_image: notificationData?.sender_image || notificationData?.image,
        duration: 8000,
        buttons: [
          {
            text: 'View Adoption Page',
            onClick: () => {
              navigate('/adoption');
            },
            primary: true
          }
        ]
      });

      updateNotificationBadgeCount();
    } catch (error) {
      console.error('❌ Error handling adoption request accepted notification:', error);
    }
  };

  const handleAdoptionRequestRejectedNotification = (notificationData) => {
    try {
      console.log('❌ Handling adoption request rejected notification:', notificationData);

      const petName = notificationData?.pet_name || 'the pet';
      const authorName = notificationData?.sender_name || notificationData?.author_name || 'The owner';

      // Show info toast notification (not error, as rejection is a normal outcome)
      showNotificationToast(notificationData, {
        type: 'info',
        title: notificationData?.title || 'Adoption Request Update',
        message: notificationData?.content || notificationData?.message || `${authorName} has declined your request to adopt ${petName}`,
        image: notificationData?.sender_image || notificationData?.image,
        sender_name: authorName,
        sender_image: notificationData?.sender_image || notificationData?.image,
        duration: 8000,
        buttons: [
          {
            text: 'Browse More Pets',
            onClick: () => {
              navigate('/adoption');
            },
            primary: true
          }
        ]
      });

      updateNotificationBadgeCount();
    } catch (error) {
      console.error('❌ Error handling adoption request rejected notification:', error);
    }
  };

  const handleNewPostNotification = (notificationData) => {
  try {
    console.log('📝 Handling new post notification:', notificationData);

    const authorName =
      notificationData?.sender_name ||
      notificationData?.user_name ||
      'Someone';

    const postTitle =
      notificationData?.post_title ||
      notificationData?.data?.post_title ||
      '';

    const postId =
      notificationData?.post_id ||
      notificationData?.data?.post_id ||
      notificationData?.target_id;

    showNotificationToast(notificationData, {
      type: 'info',
      title: notificationData?.title || `New Post from ${authorName}`,
      message:
        postTitle
          ? `${authorName} posted: ${postTitle}`
          : notificationData?.content ||
            notificationData?.message ||
            `${authorName} just posted something new!`,
      image: notificationData?.sender_image || notificationData?.image,
      sender_name: authorName,
      sender_image: notificationData?.sender_image || notificationData?.image,
      duration: 8000,
      buttons: [
        {
          text: 'View Post',
          onClick: () => {
            openPostModal(postId);
          },
          primary: true
        }
      ]
    });

    updateNotificationBadgeCount();
  } catch (error) {
    console.error('❌ Error handling new post notification:', error);
  }
};

  const handleAcceptAndTreat = (notificationData) => {
    // Close urgent case modal
    setUrgentCaseModalOpen(false);
    setUrgentCaseData(null);

    // Extract user ID from notification data for chat navigation
    let actualNotificationData = notificationData;

    // Parse nested data if it exists
    if (notificationData.data && typeof notificationData.data === 'string') {
      try {
        actualNotificationData = JSON.parse(notificationData.data);
      } catch (error) {
        console.error('❌ Error parsing nested data:', error);
      }
    }

    // Extract user ID from various possible locations in the notification data
    const userId = actualNotificationData.uid ||
      actualNotificationData.sender?.id ||
      actualNotificationData.sender_id ||
      actualNotificationData.user_id;


    // Navigate to chat with the Pet Guardian
    if (userId) {
      window.location.href = `/chat/${userId}`;
    } else {
      window.location.href = '/chat';
    }
  };

  // Notification permission modal control functions
  const closeNotificationPermissionModal = () => {
    setNotificationPermissionModalOpen(false);
  };

  const showNotificationPermissionModal = () => {
    setNotificationPermissionModalOpen(true);
  };

  // Test notification function
  const testNotification = () => {
    if (isPermissionGranted) {
      const testNotif = new Notification('Test Notification', {
        body: 'This is a test notification from Reverb',
        icon: '/favicon.ico',
      });
      setTimeout(() => testNotif.close(), 5000);
    }
  };

  return {
    isPermissionGranted,
    permissionStatus,
    isLoading,
    echoConnected,
    requestPermission,
    testNotification,
    // Adoption notification handlers
    handleAdoptionRequestReceivedNotification,
    handleAdoptionRequestAcceptedNotification,
    handleAdoptionRequestRejectedNotification,
    // Urgent case modal state and controls
    urgentCaseModalOpen,
    urgentCaseData,
    closeUrgentCaseModal,
    // Telemedicine modal state and controls
    telemedicineModalOpen,
    telemedicineData,
    closeTelemedicineModal,
    handleAcceptAndTreat,
    // Notification permission modal state and controls
    notificationPermissionModalOpen,
    closeNotificationPermissionModal,
    showNotificationPermissionModal,
    isNotificationSupported,
    // Appointment modal state and controls
    appointmentModalOpen,
    appointmentModalData,
    closeAppointmentModal,
    // Outpatient visit modal state and controls
    outpatientModalOpen,
    outpatientModalData,
    closeOutpatientModal,
    // Inpatient visit modal state and controls
    inpatientModalOpen,
    inpatientModalData,
    closeInpatientModal,
    // Post deleted by admin modal state and controls
    postDeletedModalOpen,
    postDeletedModalData,
    setPostDeletedModalOpen,
  };
};
